<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { visualToLayout } from 'dashboard/helper/dashboardZoom';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  file: {
    type: Object,
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
  saving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['save', 'cancel']);
const { t } = useI18n();

const OUTPUT_SIZE = 512;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

const cropArea = ref(null);
const imageElement = ref(null);
const sourceUrl = ref('');
const zoom = ref(MIN_ZOOM);
const offsetX = ref(0);
const offsetY = ref(0);
const isReady = ref(false);
const isDragging = ref(false);
const isSaving = ref(false);
const errorMessage = ref('');
const dragState = ref({ x: 0, y: 0, offsetX: 0, offsetY: 0 });

const revokeSourceUrl = () => {
  if (sourceUrl.value) {
    URL.revokeObjectURL(sourceUrl.value);
    sourceUrl.value = '';
  }
};

const cropLayout = (size = null) => {
  const image = imageElement.value;
  const area = cropArea.value;
  if (!image?.naturalWidth || !image?.naturalHeight) return null;

  const areaWidth = size || area?.clientWidth || OUTPUT_SIZE;
  const areaHeight = size || area?.clientHeight || OUTPUT_SIZE;
  const coverScale = Math.max(
    areaWidth / image.naturalWidth,
    areaHeight / image.naturalHeight
  );
  const scale = coverScale * zoom.value;
  const width = image.naturalWidth * scale;
  const height = image.naturalHeight * scale;

  return {
    width,
    height,
    maxOffsetX: Math.max(0, (width - areaWidth) / 2),
    maxOffsetY: Math.max(0, (height - areaHeight) / 2),
  };
};

const imageStyle = computed(() => {
  if (!isReady.value) return {};

  const layout = cropLayout();
  if (!layout) return {};

  const area = cropArea.value;
  const areaWidth = area?.clientWidth || OUTPUT_SIZE;
  const areaHeight = area?.clientHeight || OUTPUT_SIZE;

  return {
    width: `${layout.width}px`,
    height: `${layout.height}px`,
    left: `${(areaWidth - layout.width) / 2 + offsetX.value}px`,
    top: `${(areaHeight - layout.height) / 2 + offsetY.value}px`,
  };
});

const clampOffsets = () => {
  const layout = cropLayout();
  if (!layout) return;

  offsetX.value = Math.min(
    layout.maxOffsetX,
    Math.max(-layout.maxOffsetX, offsetX.value)
  );
  offsetY.value = Math.min(
    layout.maxOffsetY,
    Math.max(-layout.maxOffsetY, offsetY.value)
  );
};

const resetCrop = () => {
  zoom.value = MIN_ZOOM;
  offsetX.value = 0;
  offsetY.value = 0;
  errorMessage.value = '';
};

const setSourceFile = file => {
  revokeSourceUrl();
  isReady.value = false;
  resetCrop();

  if (file) {
    sourceUrl.value = URL.createObjectURL(file);
  }
};

const handleImageLoad = () => {
  isReady.value = true;
  clampOffsets();
};

const handleZoomInput = () => {
  clampOffsets();
};

const handlePointerDown = event => {
  if (!isReady.value) return;

  event.currentTarget.setPointerCapture(event.pointerId);
  isDragging.value = true;
  dragState.value = {
    x: event.clientX,
    y: event.clientY,
    offsetX: offsetX.value,
    offsetY: offsetY.value,
  };
};

const handlePointerMove = event => {
  if (!isDragging.value) return;

  // Aceleres 2026-08-25: delta do rato em VISUAL -> deslocamento em CSS
  // (LAYOUT). Ver helper/dashboardZoom.js.
  offsetX.value =
    dragState.value.offsetX + visualToLayout(event.clientX - dragState.value.x);
  offsetY.value =
    dragState.value.offsetY + visualToLayout(event.clientY - dragState.value.y);
  clampOffsets();
};

const handlePointerUp = event => {
  isDragging.value = false;
  if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
    event.currentTarget.releasePointerCapture(event.pointerId);
  }
};

const createCroppedFile = () => {
  const image = imageElement.value;
  const layout = cropLayout(OUTPUT_SIZE);
  if (!image || !layout) return Promise.reject(new Error('Image is not ready'));

  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const context = canvas.getContext('2d');
  if (!context) return Promise.reject(new Error('Canvas is not available'));

  const previewSize = cropArea.value?.clientWidth || OUTPUT_SIZE;
  const outputOffsetScale = OUTPUT_SIZE / previewSize;
  const drawX =
    (OUTPUT_SIZE - layout.width) / 2 + offsetX.value * outputOffsetScale;
  const drawY =
    (OUTPUT_SIZE - layout.height) / 2 + offsetY.value * outputOffsetScale;
  context.drawImage(image, drawX, drawY, layout.width, layout.height);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) {
          reject(new Error('Could not create cropped image'));
          return;
        }

        resolve(
          new File([blob], `profile-avatar-${Date.now()}.jpg`, {
            type: 'image/jpeg',
          })
        );
      },
      'image/jpeg',
      0.92
    );
  });
};

const saveAvatar = async () => {
  if (!isReady.value || isSaving.value || props.saving) return;

  isSaving.value = true;
  errorMessage.value = '';
  try {
    emit('save', await createCroppedFile());
  } catch {
    errorMessage.value = t('PROFILE_SETTINGS.FORM.ERROR');
  } finally {
    isSaving.value = false;
  }
};

watch(
  () => props.file,
  file => setSourceFile(file),
  { immediate: true }
);

onBeforeUnmount(revokeSourceUrl);
</script>

<template>
  <div
    class="flex flex-col gap-4 rounded-2xl border border-n-strong bg-n-solid-1 p-4 shadow-sm"
  >
    <div class="flex items-start justify-between gap-3">
      <div>
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ $t('PROFILE_SETTINGS.FORM.AVATAR') }}
        </h3>
        <p class="mt-1 text-sm text-n-slate-10">
          {{ $t('PROFILE_SETTINGS.FORM.UPLOAD_IMAGE') }}
        </p>
      </div>
      <button
        type="button"
        class="flex size-8 shrink-0 items-center justify-center rounded-lg text-n-slate-10 hover:bg-n-slate-2 hover:text-n-slate-12"
        :aria-label="$t('GENERAL.CLOSE')"
        @click="emit('cancel')"
      >
        <i class="i-lucide-x size-4" aria-hidden="true" />
      </button>
    </div>

    <div
      ref="cropArea"
      class="relative mx-auto aspect-square w-full max-w-72 touch-none select-none overflow-hidden rounded-full bg-n-slate-3"
      :class="{ 'cursor-grab': !isDragging, 'cursor-grabbing': isDragging }"
      @pointerdown="handlePointerDown"
      @pointermove="handlePointerMove"
      @pointerup="handlePointerUp"
      @pointercancel="handlePointerUp"
    >
      <div class="absolute inset-0">
        <img
          ref="imageElement"
          :src="sourceUrl"
          :alt="name"
          class="pointer-events-none absolute block max-w-none select-none"
          :style="imageStyle"
          draggable="false"
          @load="handleImageLoad"
        />
      </div>
      <div
        class="pointer-events-none absolute inset-0 rounded-full ring-2 ring-white/80"
      />
    </div>

    <label class="flex items-center gap-3 text-sm font-medium text-n-slate-12">
      <i class="i-lucide-minus size-4 text-n-slate-10" aria-hidden="true" />
      <input
        v-model.number="zoom"
        class="min-w-0 flex-1 accent-n-brand"
        type="range"
        :min="MIN_ZOOM"
        :max="MAX_ZOOM"
        step="0.05"
        :aria-label="$t('PROFILE_SETTINGS.FORM.AVATAR')"
        @input="handleZoomInput"
      />
      <i class="i-lucide-plus size-4 text-n-slate-10" aria-hidden="true" />
    </label>

    <p v-if="errorMessage" class="text-sm text-n-ruby-11" role="alert">
      {{ errorMessage }}
    </p>

    <div class="flex justify-end gap-2">
      <button
        type="button"
        class="rounded-lg border border-n-strong bg-n-solid-1 px-3 py-2 text-sm font-medium text-n-slate-11 shadow-sm transition-colors duration-150 hover:border-n-brand hover:bg-n-brand/5 hover:text-n-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand/30"
        @click="emit('cancel')"
      >
        {{ $t('GENERAL.CLOSE') }}
      </button>
      <button
        type="button"
        class="rounded-lg border border-n-brand bg-n-brand px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors duration-150 hover:border-n-blue-10 hover:bg-n-blue-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand/30 disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="!isReady || isSaving || saving"
        @click="saveAvatar"
      >
        {{ $t('PROFILE_SETTINGS.FORM.UPDATE_IMAGE') }}
      </button>
    </div>
  </div>
</template>
